/**
 * `<ts-exit-intent>` — exit-intent campaign controller.
 *
 * Ported from the legacy theme's `src/js/exit-intent/exit-intent.tsx` (Preact)
 * plus `suppression.ts` and `page-rules.ts`; the behavior is unchanged, see
 * the legacy `docs/EXIT-INTENT.md` for the merchant-facing rules. Rendered
 * once per page by snippets/ts-exit-intent.liquid, which also serializes the
 * `exit_intent_campaign` metaobject entries into the `campaigns` JSON ref.
 *
 * Flow:
 * 1. Campaigns are normalized (defaults filled in) and sorted by priority.
 * 2. Every campaign that matches the device, isn't suppressed and isn't
 *    suppressed on this page has its triggers armed (after the page delay,
 *    if one matches) — see ts-exit-intent-triggers.js.
 * 3. When a trigger fires, the cart is fetched and the first campaign that
 *    passes device / suppression / page / country / cart-range checks shows
 *    in its layout — see ts-exit-intent-widgets.js.
 * 4. Dismissal marks the campaign (and, if configured, all campaigns)
 *    suppressed in storage and re-arms whatever is still eligible.
 *
 * Theme integration (TS): cart changes arrive as Horizon's `cart:update`
 * (`ThemeEvents.cartUpdate`) instead of the legacy `ts:cart:updated:*`, and
 * "mini-cart closed" is the cart drawer's `dialog:close` (`DialogCloseEvent`).
 *
 * Debug URL parameters (unchanged): `?ts_ei_force=<id|name|any>` shows a
 * campaign bypassing every gate, `?ts_ei_clear` wipes suppression storage,
 * `?ts_ei_debug` logs match/skip reasons to the console.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';
import { createOrchestrator, isMobile } from '@ts/exit-intent-triggers';
import { createWidget, isWidgetAvailable } from '@ts/exit-intent-widgets';

/**
 * @typedef {import('@ts/exit-intent-triggers').TriggerKind} TriggerKind
 * @typedef {import('@ts/exit-intent-triggers').TriggerHost} TriggerHost
 * @typedef {import('@ts/exit-intent-triggers').Orchestrator} Orchestrator
 * @typedef {import('@ts/exit-intent-triggers').CampaignTriggers} CampaignTriggers
 * @typedef {import('@ts/exit-intent-triggers').PredictiveConfig} PredictiveConfig
 * @typedef {import('@ts/exit-intent-triggers').BackbuttonConfig} BackbuttonConfig
 * @typedef {import('@ts/exit-intent-triggers').ScrollConfig} ScrollConfig
 * @typedef {import('@ts/exit-intent-triggers').ProactiveConfig} ProactiveConfig
 * @typedef {import('@ts/exit-intent-widgets').WidgetConfig} WidgetConfig
 * @typedef {import('@ts/exit-intent-widgets').WidgetType} WidgetType
 * @typedef {import('@ts/exit-intent-widgets').DismissMode} DismissMode
 * @typedef {import('@ts/exit-intent-widgets').InjectPosition} InjectPosition
 * @typedef {import('@ts/exit-intent-widgets').WidgetInstance} WidgetInstance
 * @typedef {import('@ts/exit-intent-widgets').Cart} Cart
 *
 * @typedef {'desktop' | 'mobile' | 'both'} DeviceTarget
 * @typedef {'desktop' | 'mobile'} CurrentDevice
 * @typedef {'none' | 'session' | '24h' | '72h' | '30d'} SuppressionStrategy
 *
 * @typedef {object} SuppressionConfig
 * @property {SuppressionStrategy} self_suppression
 * @property {boolean} respects_global_suppression
 * @property {boolean} contributes_to_global_suppression
 *
 * @typedef {object} Campaign
 * @property {string} id - Metaobject GID.
 * @property {string} name - Internal name (never shown).
 * @property {number} min_cart_subtotal - Dollars; 0 = no minimum.
 * @property {number} max_cart_subtotal - Dollars; 0 = no maximum.
 * @property {string} countries - Comma-separated ISO codes; empty = any.
 * @property {string} header
 * @property {string} footer
 * @property {string} cta_text
 * @property {string} cta_url
 * @property {number} priority - Lower wins.
 * @property {DeviceTarget} device
 * @property {string} [background_color]
 * @property {string} [text_color]
 * @property {boolean} [show_cart_items]
 * @property {string[]} page_suppression - Path patterns where the campaign never arms.
 * @property {string[]} page_delays - `"pattern | seconds"` rules delaying arming.
 * @property {WidgetConfig} widget
 * @property {SuppressionConfig} suppression
 * @property {CampaignTriggers} triggers
 *
 * @typedef {object} TsExitIntentRefs
 * @property {HTMLScriptElement} campaigns - `application/json` payload of the campaigns.
 */

// ---------------------------------------------------------------------------
// Debug logging — active when ?ts_ei_debug is present in the URL.
// ---------------------------------------------------------------------------

const DEBUG = (() => {
  try {
    return new URLSearchParams(window.location.search).has('ts_ei_debug');
  } catch {
    return false;
  }
})();

/**
 * @param {string} message
 * @param {...unknown} args
 */
const dbg = (message, ...args) => {
  if (!DEBUG) return;
  console.log(`%c[exit-intent]%c ${message}`, 'color:#6a8fff;font-weight:bold', 'color:inherit', ...args);
};

// ---------------------------------------------------------------------------
// Defaults and normalization
// ---------------------------------------------------------------------------

const TAB_MAX_AGE_MS = 86_400_000;

/** @type {PredictiveConfig} */
const PREDICTIVE_DEFAULTS = {
  enabled: false,
  exit_area_px: 20,
  time_hover_requirement: 1000,
  speed_upward: 750,
  time_above_y: 500,
  no_pop_quadrant: 0.15,
  any_trajectory: 0,
  tab_detection_time: 1000,
  content_start: 0,
  skip_no_pop_on_mac: true,
};

/** @type {BackbuttonConfig} */
const BACKBUTTON_DEFAULTS = {
  enabled: false,
  backtrap_load_delay: 0,
  backtrap_placement_delay: 50,
  backtrap_timestamp_difference: 100,
  heartbeat_max_gap: 2500,
  iphone_first_click: true,
  back_means_exit: true,
};

/** @type {ScrollConfig} */
const SCROLL_DEFAULTS = {
  enabled: false,
  scroll_placement_delay: 5000,
  poll_interval: 500,
  window_ratio: 1.5,
  activation_start: 25,
  activation_unit: '%',
  require_scroll_up: true,
  scroll_up_speed: 4000,
  launch_start: 0,
  launch_unit: '%',
  scroll_top_launch_delay: 0,
};

/** @type {ProactiveConfig} */
const PROACTIVE_DEFAULTS = {
  enabled: false,
  proactive_idle: false,
  launch_delay: 30000,
};

/** @type {WidgetConfig} */
const WIDGET_DEFAULTS = {
  type: 'injected',
  inject_selector: '',
  inject_position: 'prepend',
  inject_dispatch_event: '',
  dismiss_modes: ['close_button', 'esc_key', 'outside_click'],
};

/** @type {SuppressionConfig} */
const SUPPRESSION_DEFAULTS = {
  self_suppression: 'session',
  respects_global_suppression: true,
  contributes_to_global_suppression: true,
};

/** @type {DismissMode[]} */
const DISMISS_MODES = ['close_button', 'esc_key', 'outside_click'];
/** @type {WidgetType[]} */
const WIDGET_TYPES = ['injected', 'slideout_left', 'slideout_right', 'slideout_bottom', 'popup_center'];
/** @type {InjectPosition[]} */
const INJECT_POSITIONS = ['prepend', 'append', 'before', 'after'];
/** @type {SuppressionStrategy[]} */
const SUPPRESSION_STRATEGIES = ['none', 'session', '24h', '72h', '30d'];

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
const isObject = (value) => typeof value === 'object' && value !== null;

/**
 * @template {string} T
 * @param {unknown} value
 * @param {readonly T[]} allowed
 * @param {T} fallback
 * @returns {T}
 */
const oneOf = (value, allowed, fallback) =>
  typeof value === 'string' && allowed.includes(/** @type {T} */ (value)) ? /** @type {T} */ (value) : fallback;

/**
 * @param {unknown} input
 * @returns {DismissMode[]}
 */
const parseDismissModes = (input) => {
  /** @param {unknown} mode @returns {mode is DismissMode} */
  const allowed = (mode) => typeof mode === 'string' && DISMISS_MODES.includes(/** @type {DismissMode} */ (mode));
  if (Array.isArray(input)) return input.filter(allowed);
  if (typeof input === 'string') {
    return input
      .split(',')
      .map((mode) => mode.trim())
      .filter(allowed);
  }
  return [...WIDGET_DEFAULTS.dismiss_modes];
};

/**
 * @param {unknown} raw
 * @returns {WidgetConfig}
 */
const parseWidget = (raw) => {
  const r = isObject(raw) ? raw : {};
  return {
    type: oneOf(r.type, WIDGET_TYPES, WIDGET_DEFAULTS.type),
    inject_selector:
      typeof r.inject_selector === 'string' && r.inject_selector ? r.inject_selector : WIDGET_DEFAULTS.inject_selector,
    inject_position: oneOf(r.inject_position, INJECT_POSITIONS, WIDGET_DEFAULTS.inject_position),
    inject_dispatch_event:
      typeof r.inject_dispatch_event === 'string' ? r.inject_dispatch_event : WIDGET_DEFAULTS.inject_dispatch_event,
    dismiss_modes: parseDismissModes(r.dismiss_modes),
  };
};

/**
 * @param {unknown} raw
 * @returns {SuppressionConfig}
 */
const parseSuppression = (raw) => {
  const r = isObject(raw) ? raw : {};
  return {
    self_suppression: oneOf(r.self_suppression, SUPPRESSION_STRATEGIES, SUPPRESSION_DEFAULTS.self_suppression),
    respects_global_suppression:
      typeof r.respects_global_suppression === 'boolean'
        ? r.respects_global_suppression
        : SUPPRESSION_DEFAULTS.respects_global_suppression,
    contributes_to_global_suppression:
      typeof r.contributes_to_global_suppression === 'boolean'
        ? r.contributes_to_global_suppression
        : SUPPRESSION_DEFAULTS.contributes_to_global_suppression,
  };
};

/**
 * @template {object} T
 * @param {unknown} raw
 * @param {T} defaults
 * @returns {T}
 */
const fillDefaults = (raw, defaults) => {
  if (!isObject(raw)) return { ...defaults };
  const out = /** @type {Record<string, unknown>} */ ({ ...defaults });
  for (const key of Object.keys(defaults)) {
    const value = raw[key];
    if (value !== undefined && value !== null) out[key] = value;
  }
  return /** @type {T} */ (out);
};

/**
 * @param {DeviceTarget} device
 * @returns {CampaignTriggers}
 */
const getDefaultTriggers = (device) => {
  switch (device) {
    case 'desktop':
      return { predictive: { ...PREDICTIVE_DEFAULTS, enabled: true } };
    case 'mobile':
      return {
        backbutton: { ...BACKBUTTON_DEFAULTS, enabled: true },
        scroll: { ...SCROLL_DEFAULTS, enabled: true },
      };
    default:
      return {
        predictive: { ...PREDICTIVE_DEFAULTS, enabled: true },
        backbutton: { ...BACKBUTTON_DEFAULTS, enabled: true },
        scroll: { ...SCROLL_DEFAULTS, enabled: true },
      };
  }
};

/**
 * @param {unknown} raw
 * @param {DeviceTarget} device
 * @returns {CampaignTriggers}
 */
const parseTriggers = (raw, device) => {
  if (!isObject(raw)) return getDefaultTriggers(device);

  /** @type {CampaignTriggers} */
  const result = {};
  if (raw.predictive) result.predictive = fillDefaults(raw.predictive, PREDICTIVE_DEFAULTS);
  if (raw.backbutton) result.backbutton = fillDefaults(raw.backbutton, BACKBUTTON_DEFAULTS);
  if (raw.scroll) result.scroll = fillDefaults(raw.scroll, SCROLL_DEFAULTS);
  if (raw.proactive) result.proactive = fillDefaults(raw.proactive, PROACTIVE_DEFAULTS);

  return Object.keys(result).length === 0 ? getDefaultTriggers(device) : result;
};

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
const parseStringList = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.filter((value) => typeof value === 'string' && value.trim() !== '');
};

/**
 * @param {unknown} raw
 * @returns {Campaign[]}
 */
const parseCampaigns = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(isObject)
    .map((c) => {
      const device = oneOf(c.device, /** @type {DeviceTarget[]} */ (['desktop', 'mobile', 'both']), 'both');
      /** @type {Campaign} */
      const campaign = {
        id: String(c.id),
        name: typeof c.name === 'string' ? c.name : '',
        min_cart_subtotal: Number(c.min_cart_subtotal) || 0,
        max_cart_subtotal: Number(c.max_cart_subtotal) || 0,
        countries: typeof c.countries === 'string' ? c.countries : '',
        header: typeof c.header === 'string' ? c.header : '',
        footer: typeof c.footer === 'string' ? c.footer : '',
        cta_text: typeof c.cta_text === 'string' ? c.cta_text : '',
        cta_url: typeof c.cta_url === 'string' ? c.cta_url : '',
        priority: Number(c.priority) || 0,
        device,
        background_color: typeof c.background_color === 'string' ? c.background_color : undefined,
        text_color: typeof c.text_color === 'string' ? c.text_color : undefined,
        show_cart_items: c.show_cart_items === true,
        page_suppression: parseStringList(c.page_suppression),
        page_delays: parseStringList(c.page_delays),
        widget: parseWidget(c.widget),
        suppression: parseSuppression(c.suppression),
        triggers: parseTriggers(c.triggers, device),
      };
      return campaign;
    })
    .sort((a, b) => a.priority - b.priority);
};

// ---------------------------------------------------------------------------
// Suppression (storage)
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'ts_ei_';
const GLOBAL_KEY = 'ts_ei_global';
const GLOBAL_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/** @type {Record<'24h' | '72h' | '30d', number>} */
const TTL_MAP = {
  '24h': 86_400_000,
  '72h': 259_200_000,
  '30d': 2_592_000_000,
};

/**
 * @param {Storage} store
 * @param {string} key
 * @returns {string | null}
 */
const safeGet = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

/**
 * @param {Storage} store
 * @param {string} key
 * @param {string} value
 */
const safeSet = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch {
    // storage unavailable
  }
};

/**
 * @param {Storage} store
 * @param {string} key
 */
const safeRemove = (store, key) => {
  try {
    store.removeItem(key);
  } catch {
    // storage unavailable
  }
};

/**
 * @param {string} key
 * @param {number} ttl
 * @returns {boolean} Whether a non-expired timestamp is stored (expired ones are removed).
 */
const timestampActive = (key, ttl) => {
  const stored = safeGet(localStorage, key);
  if (stored === null) return false;

  const timestamp = parseInt(stored, 10);
  if (Number.isNaN(timestamp)) return false;
  if (Date.now() - timestamp < ttl) return true;

  safeRemove(localStorage, key);
  return false;
};

/**
 * @param {Campaign} campaign
 * @returns {boolean}
 */
const selfSuppressed = (campaign) => {
  const strategy = campaign.suppression.self_suppression;
  const key = `${STORAGE_PREFIX}${campaign.id}`;

  if (strategy === 'none') return false;
  if (strategy === 'session') return safeGet(sessionStorage, key) !== null;
  return timestampActive(key, TTL_MAP[strategy]);
};

/**
 * @param {Campaign} campaign
 * @returns {boolean}
 */
const isSuppressed = (campaign) => {
  if (selfSuppressed(campaign)) return true;
  return campaign.suppression.respects_global_suppression && timestampActive(GLOBAL_KEY, GLOBAL_WINDOW_MS);
};

/** @param {Campaign} campaign */
const markSuppressed = (campaign) => {
  const strategy = campaign.suppression.self_suppression;
  const key = `${STORAGE_PREFIX}${campaign.id}`;
  const now = String(Date.now());

  if (strategy === 'session') safeSet(sessionStorage, key, now);
  else if (strategy !== 'none') safeSet(localStorage, key, now);

  if (campaign.suppression.contributes_to_global_suppression) safeSet(localStorage, GLOBAL_KEY, now);
};

/** `?ts_ei_clear` — wipe all exit-intent storage so a test starts clean. */
const maybeClearStorage = () => {
  try {
    if (!new URLSearchParams(window.location.search).has('ts_ei_clear')) return;
    for (const store of [localStorage, sessionStorage]) {
      /** @type {string[]} */
      const keys = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
      }
      for (const key of keys) store.removeItem(key);
    }
  } catch {
    // storage unavailable
  }
};

// ---------------------------------------------------------------------------
// Page rules
// ---------------------------------------------------------------------------

/** @param {string} s */
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Normalize a path or pattern for comparison: trim, lowercase, drop the
 * trailing slash (except for the bare "/" homepage path).
 *
 * @param {string} path
 */
const normalizePath = (path) => {
  const p = path.trim().toLowerCase();
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
};

/**
 * Exact match unless the pattern contains `*`, which matches any sequence of
 * characters. Matched against location.pathname (no query string).
 *
 * @param {string} pathname
 * @param {string} pattern
 * @returns {boolean}
 */
const matchesPattern = (pathname, pattern) => {
  const path = normalizePath(pathname);
  const pat = normalizePath(pattern);
  if (!pat) return false;
  if (!pat.includes('*')) return path === pat;
  return new RegExp(`^${pat.split('*').map(escapeRegExp).join('.*')}$`).test(path);
};

/**
 * @param {Campaign} campaign
 * @param {string} pathname
 * @returns {boolean}
 */
const isPageSuppressed = (campaign, pathname) =>
  campaign.page_suppression.some((pattern) => matchesPattern(pathname, pattern));

/**
 * Each entry has the form "pattern | seconds" (e.g. "/products/* | 10"). The
 * first matching entry wins. Returns 0 when no entry matches.
 *
 * @param {Campaign} campaign
 * @param {string} pathname
 * @returns {number} Delay in ms.
 */
const getPageDelayMs = (campaign, pathname) => {
  for (const rule of campaign.page_delays) {
    const sep = rule.lastIndexOf('|');
    if (sep === -1) continue;
    const seconds = parseFloat(rule.slice(sep + 1));
    if (Number.isNaN(seconds) || seconds <= 0) continue;
    if (matchesPattern(pathname, rule.slice(0, sep))) return Math.round(seconds * 1000);
  }
  return 0;
};

// ---------------------------------------------------------------------------
// Eligibility
// ---------------------------------------------------------------------------

/**
 * @param {string} countries
 * @returns {string[]}
 */
const parseCountries = (countries) =>
  countries
    .split(',')
    .map((code) => code.trim().toUpperCase())
    .filter(Boolean);

/** @returns {Promise<string | null>} Detected country code, upper-cased. */
const fetchBrowsingContext = async () => {
  try {
    const response = await fetch('/browsing_context_suggestions.json');
    const data = await response.json();
    const handle = data?.detected_values?.country?.handle;
    return typeof handle === 'string' ? handle.toUpperCase() : null;
  } catch {
    return null;
  }
};

/** @returns {Promise<Cart | null>} */
const fetchCart = async () => {
  try {
    const response = await fetch('/cart.js');
    return await response.json();
  } catch {
    return null;
  }
};

/** @param {number} dollars */
const toCents = (dollars) => Math.round(dollars * 100);

/**
 * @param {DeviceTarget} target
 * @param {CurrentDevice} current
 */
const deviceMatches = (target, current) => target === 'both' || target === current;

/**
 * @param {Campaign} campaign
 * @param {Cart} cart
 */
const cartMatches = (campaign, cart) => {
  const maxCents = toCents(campaign.max_cart_subtotal);
  const aboveMin = cart.total_price >= toCents(campaign.min_cart_subtotal);
  const withinMax = maxCents === 0 || cart.total_price <= maxCents;
  return aboveMin && withinMax;
};

/**
 * @param {Campaign} campaign
 * @param {string | null} country
 */
const countryMatches = (campaign, country) => {
  const allowed = parseCountries(campaign.countries);
  if (allowed.length === 0) return true;
  if (!country) return false;
  return allowed.includes(country);
};

/** Campaign ids whose injected host was missing on this page — warned about once. */
const unavailableWarned = new Set();

/** @param {Campaign} campaign */
const warnUnavailableOnce = (campaign) => {
  if (unavailableWarned.has(campaign.id)) return;
  unavailableWarned.add(campaign.id);
  console.warn(
    `[exit-intent] campaign "${campaign.name}" (${campaign.id}) is type=injected but inject_selector=${JSON.stringify(
      campaign.widget.inject_selector
    )} matched no element on this page. Configure widget_inject_selector in the metaobject.`
  );
};

/**
 * First campaign (already sorted by priority) that passes every gate.
 *
 * @param {Campaign[]} campaigns
 * @param {Cart} cart
 * @param {string | null} country
 * @param {CurrentDevice} device
 * @param {number} armedAtMs
 * @returns {Campaign | null}
 */
const matchCampaign = (campaigns, cart, country, device, armedAtMs) => {
  const pathname = window.location.pathname;
  dbg(
    `matching ${campaigns.length} campaign(s) — device: ${device}, country: ${country ?? 'unknown'}, cart: ${cart.total_price}¢`
  );

  for (const campaign of campaigns) {
    const label = `"${campaign.name}"`;
    if (!deviceMatches(campaign.device, device)) {
      dbg(`  skip ${label} — device mismatch (campaign: ${campaign.device})`);
      continue;
    }
    if (isSuppressed(campaign)) {
      dbg(`  skip ${label} — suppressed`);
      continue;
    }
    if (isPageSuppressed(campaign, pathname)) {
      dbg(`  skip ${label} — page suppressed (${pathname})`);
      continue;
    }
    if (!isWidgetAvailable(campaign)) {
      // Skipped, not shown: the campaign must not be marked suppressed for a host it never rendered into.
      dbg(`  skip ${label} — inject selector matched nothing (${campaign.widget.inject_selector || 'empty'})`);
      warnUnavailableOnce(campaign);
      continue;
    }
    const delayMs = getPageDelayMs(campaign, pathname);
    if (delayMs > 0 && Date.now() - armedAtMs < delayMs) {
      dbg(`  skip ${label} — page delay pending (${delayMs - (Date.now() - armedAtMs)}ms left)`);
      continue;
    }
    if (!countryMatches(campaign, country)) {
      dbg(`  skip ${label} — country not matched (allowed: ${campaign.countries || 'any'})`);
      continue;
    }
    if (!cartMatches(campaign, cart)) {
      dbg(
        `  skip ${label} — cart ${cart.total_price}¢ outside [${campaign.min_cart_subtotal * 100}, ${
          campaign.max_cart_subtotal ? campaign.max_cart_subtotal * 100 : '∞'
        }]`
      );
      continue;
    }
    dbg(`  match ${label} ✓`);
    return campaign;
  }

  dbg('  no campaign matched');
  return null;
};

/**
 * `?ts_ei_force=<id|name|any>` — bypass all gates for testing. Matches, in
 * order: full GID, numeric tail of the GID, exact name, case-insensitive name.
 *
 * @param {Campaign[]} campaigns
 * @returns {Campaign | null}
 */
const getForcedCampaign = (campaigns) => {
  try {
    const force = new URLSearchParams(window.location.search).get('ts_ei_force');
    if (!force) return null;
    if (force === 'any') return campaigns[0] ?? null;

    const byId = campaigns.find((c) => c.id === force) ?? campaigns.find((c) => c.id.endsWith(force));
    if (byId) return byId;

    const lower = force.toLowerCase();
    const byName = campaigns.find((c) => c.name.toLowerCase() === lower);
    if (byName) return byName;

    console.warn(
      `[exit-intent] ts_ei_force="${force}" matched no campaign. Available:`,
      campaigns.map((c) => ({ id: c.id, name: c.name }))
    );
    return null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

/**
 * @extends {Component<TsExitIntentRefs>}
 */
class TsExitIntent extends Component {
  requiredRefs = ['campaigns'];

  /** @type {Campaign[]} */
  #campaigns = [];

  /** @type {Orchestrator | null} */
  #orchestrator = null;

  /**
   * Detected country, resolved on first use (see #getCountry).
   * @type {Promise<string | null> | null}
   */
  #countryPromise = null;

  #armedAt = Date.now();

  /** @type {{ id: string, kind: TriggerKind } | null} */
  #pendingFire = null;

  /** @type {CurrentDevice} */
  #device = 'desktop';

  /** @type {Campaign | null} */
  #forced = null;

  /** @type {number[]} */
  #armDelayTimers = [];

  /** @type {Campaign | null} */
  #active = null;

  /** @type {WidgetInstance | null} */
  #widget = null;

  #visible = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#campaigns = parseCampaigns(this.#readCampaigns());
    if (this.#campaigns.length === 0) return;

    this.#device = isMobile() ? 'mobile' : 'desktop';
    this.#forced = getForcedCampaign(this.#campaigns);

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdated, { signal });
    // Capture phase: DialogCloseEvent doesn't bubble.
    document.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose, { capture: true, signal });

    maybeClearStorage();
    dbg(
      `init — ${this.#campaigns.length} campaign(s) loaded`,
      this.#campaigns.map((c) => ({ id: c.id, name: c.name, device: c.device }))
    );

    if (this.#forced) {
      this.#forceShow(this.#forced);
      return;
    }

    this.#armEligible();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#clearArmDelays();
    this.#orchestrator?.destroyAll();
    this.#orchestrator = null;
    this.#hide();
  }

  /** @returns {unknown} */
  #readCampaigns() {
    try {
      return JSON.parse(this.refs.campaigns.textContent || '[]');
    } catch (error) {
      console.error('[exit-intent] invalid campaigns payload', error);
      return [];
    }
  }

  #clearArmDelays() {
    for (const timer of this.#armDelayTimers) window.clearTimeout(timer);
    this.#armDelayTimers = [];
  }

  /**
   * Formats minor units with the shop's money format.
   * @param {number} cents
   */
  #formatMoney = (cents) =>
    formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', window.Shopify?.currency?.active ?? 'USD');

  /**
   * @param {Cart | null} cart
   * @returns {import('@ts/exit-intent-widgets').WidgetContext}
   */
  #widgetContext(cart) {
    return {
      onDismiss: this.#handleDismiss,
      cart,
      formatMoney: this.#formatMoney,
      logoUrl: this.dataset.logoUrl,
      strings: {
        close: this.dataset.textClose ?? '',
        itemsInCart: this.dataset.textItemsInCart ?? '',
        total: this.dataset.textTotal ?? '',
        quantity: this.dataset.textQuantity ?? '',
      },
    };
  }

  /**
   * The shopper's detected country, fetched once and only when a campaign
   * actually filters by country — so shoppers on stores without geo-targeted
   * campaigns never pay for the request. Resolved at the first trigger, in
   * parallel with the cart fetch.
   *
   * @returns {Promise<string | null>}
   */
  #getCountry() {
    this.#countryPromise ??= this.#campaigns.some((campaign) => parseCountries(campaign.countries).length > 0)
      ? fetchBrowsingContext()
      : Promise.resolve(null);
    return this.#countryPromise;
  }

  /**
   * Shows a campaign in its widget. `animate` is false for an in-place swap
   * (cart re-evaluation) so the replacement doesn't slide in again.
   *
   * @param {Campaign} campaign
   * @param {Cart | null} cart
   * @param {boolean} [animate]
   */
  #show(campaign, cart, animate = true) {
    this.#widget?.destroy();
    this.#active = campaign;
    this.#visible = true;
    this.#widget = createWidget(campaign, this.#widgetContext(cart));
    if (this.#widget?.show(animate)) return;

    // The layout couldn't be placed (an injected host vanished since matching,
    // or an unknown layout). Drop it without marking suppression — nothing was
    // shown — and let the remaining campaigns re-arm.
    dbg(`  could not place "${campaign.name}" — hidden without suppression`);
    this.#hide();
    if (!this.#forced) this.#armEligible();
  }

  #hide() {
    this.#widget?.destroy();
    this.#widget = null;
    this.#active = null;
    this.#visible = false;
  }

  /** @param {Campaign} campaign */
  async #forceShow(campaign) {
    dbg(`force-showing "${campaign.name}"`);
    const cart = campaign.show_cart_items ? await fetchCart() : null;
    if (!this.isConnected) return;
    this.#show(campaign, cart);
  }

  /**
   * A trigger fired for `firingId`: re-check eligibility and show the winner.
   *
   * @param {string} firingId
   * @param {TriggerKind} kind
   */
  #handleTrigger = async (firingId, kind) => {
    dbg(`trigger fired: ${kind} (campaign id: ${firingId})`);

    if (Date.now() - this.#armedAt > TAB_MAX_AGE_MS) {
      dbg('  blocked — tab too old');
      this.#orchestrator?.destroyAll();
      this.#orchestrator = null;
      return;
    }

    if (this.#visible || this.#pendingFire) {
      dbg(`  blocked — ${this.#visible ? 'widget already visible' : 'fire already pending'}`);
      return;
    }
    this.#pendingFire = { id: firingId, kind };

    const [cart, country] = await Promise.all([fetchCart(), this.#getCountry()]);
    // The fire may have been cancelled (tab switch) while the cart loaded.
    if (this.#pendingFire?.id !== firingId) return;
    if (!cart) {
      dbg('  blocked — cart fetch failed');
      this.#pendingFire = null;
      return;
    }

    const matched = matchCampaign(this.#campaigns, cart, country, this.#device, this.#armedAt);
    this.#pendingFire = null;
    if (!matched) return;

    dbg(`showing "${matched.name}"`);
    this.#show(matched, cart);
  };

  /**
   * Cancel a fire that was just requested but hasn't shown yet, or hide the
   * widget currently showing for this campaign (predictive tab-detection
   * cancel window).
   *
   * @param {string} cancelingId
   */
  #handleCancel = (cancelingId) => {
    if (this.#pendingFire?.id === cancelingId) this.#pendingFire = null;
    if (this.#active?.id === cancelingId) this.#hide();
  };

  /** Arms the triggers of every currently eligible campaign. */
  #armEligible() {
    this.#clearArmDelays();

    this.#orchestrator?.destroyAll();
    const orchestrator = createOrchestrator();
    this.#orchestrator = orchestrator;

    if (orchestrator.isIframe()) {
      dbg('skipping arm — running inside iframe');
      return;
    }

    this.#armedAt = Date.now();
    dbg(`arming — device: ${this.#device}, ${this.#campaigns.length} campaign(s)`);

    const pathname = window.location.pathname;

    for (const campaign of this.#campaigns) {
      const label = `"${campaign.name}"`;
      if (!deviceMatches(campaign.device, this.#device)) {
        dbg(`  skip ${label} — device mismatch (campaign: ${campaign.device})`);
        continue;
      }
      if (isSuppressed(campaign)) {
        dbg(`  skip ${label} — suppressed`);
        continue;
      }
      if (isPageSuppressed(campaign, pathname)) {
        dbg(`  skip ${label} — page suppressed (${pathname})`);
        continue;
      }

      const triggerNames = Object.entries(campaign.triggers)
        .filter(([, config]) => config?.enabled)
        .map(([name]) => name)
        .join(', ');

      /** @type {TriggerHost} */
      const host = {
        requestFire: (kind) => this.#handleTrigger(campaign.id, kind),
        cancelFire: () => this.#handleCancel(campaign.id),
      };

      const delayMs = getPageDelayMs(campaign, pathname);
      if (delayMs > 0) {
        dbg(`  arm ${label} in ${delayMs}ms — page delay matched (${pathname})`);
        const timer = window.setTimeout(() => {
          // Bail if triggers were re-armed (or torn down) while waiting, or
          // the campaign got suppressed by another dismissal in the meantime.
          if (this.#orchestrator !== orchestrator) return;
          if (isSuppressed(campaign)) {
            dbg(`  skip ${label} — suppressed during page delay`);
            return;
          }
          dbg(`  arm ${label} — page delay elapsed, triggers: [${triggerNames || 'none'}]`);
          orchestrator.armCampaign(campaign, host);
        }, delayMs);
        this.#armDelayTimers.push(timer);
        continue;
      }

      dbg(`  arm ${label} — triggers: [${triggerNames || 'none'}]`);
      orchestrator.armCampaign(campaign, host);
    }
  }

  #handleDismiss = () => {
    // In force mode, just close — don't mark suppression or re-arm triggers.
    if (this.#forced) {
      this.#forced = null;
      this.#hide();
      return;
    }

    if (this.#active) markSuppressed(this.#active);
    this.#hide();
    this.#armEligible();
  };

  /**
   * TS: the legacy `ts:mini-cart:closed` hook. A campaign injected into the
   * cart drawer is dismissed when the drawer closes.
   *
   * @param {Event} event
   */
  #onDialogClose = (event) => {
    if (!(event.target instanceof Element) || event.target.localName !== 'cart-drawer-component') return;
    if (!this.#visible || this.#active?.widget.type !== 'injected') return;
    this.#handleDismiss();
  };

  /**
   * Re-evaluate the match when the cart changes while a widget is showing:
   * refresh the cart preview if the campaign still matches, otherwise swap to
   * another matching campaign or close.
   */
  #onCartUpdated = async () => {
    if (!this.#visible || !this.#active) return;

    const [cart, country] = await Promise.all([fetchCart(), this.#getCountry()]);
    if (!cart || !this.#visible || !this.#active) return;
    if (cartMatches(this.#active, cart)) {
      if (this.#active.show_cart_items) this.#widget?.update(cart);
      return;
    }

    const newMatch = matchCampaign(this.#campaigns, cart, country, this.#device, this.#armedAt);
    if (newMatch) {
      this.#show(newMatch, cart, false);
    } else {
      this.#hide();
    }
  };
}

if (!customElements.get('ts-exit-intent')) customElements.define('ts-exit-intent', TsExitIntent);
